import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import DocumentActions from 'notebook/documents/DocumentActions';
import DocumentStore from 'notebook/documents/DocumentStore';
import {
  WorksheetViewStateAction,
  WorksheetViewActionType,
  WorksheetViewState,
} from 'notebook/worksheets/WorksheetConstants';
import {WorksheetViewQuery} from 'notebook/worksheets/workspace/__generated/WorksheetViewQuery';
import notifications from 'notifications';
import {Thunk} from 'ui-library/hooks/useReducerWithThunk';
import removeNulls from 'util/removeNulls';

type WorksheetOrInaccessibleGraphQL = WorksheetViewQuery['worksheet'];

export type WorksheetViewStateThunk = Thunk<WorksheetViewState, WorksheetViewStateAction>;

/**
 * A thunk action that fetches all documents in a worksheet.
 * Fetching the documents is an async operation, so we use a thunk to handle it.
 */
export function fetchAllDocumentsInWorksheet(
  worksheet: WorksheetOrInaccessibleGraphQL
): WorksheetViewStateThunk {
  return async (dispatch) => {
    // Always dispatch a loading state first so that the UI can reflect that step documents are being fetched.
    dispatch({type: WorksheetViewActionType.LOADED, worksheet, steps: null});

    if (isInaccessible(worksheet)) {
      dispatch({type: WorksheetViewActionType.LOADED, worksheet, steps: []});
      return;
    }

    const docIds = worksheet.worksheetStepGroups.flatMap((group) =>
      group.worksheetSteps.map((step) => step.document.id)
    );

    // Always trigger a fresh fetch for all documents.
    const fetchedAll = await DocumentActions.openMany(docIds);
    if (!fetchedAll) {
      dispatch({type: WorksheetViewActionType.FAILED_TO_LOAD_STEPS});
      return;
    }

    // Once all documents are loaded, retrieve them from the store and update the state.
    const loadedDocs = removeNulls(docIds.map((docId) => DocumentStore.getDocument(docId)));

    if (loadedDocs.length !== docIds.length) {
      notifications.error('Failed to load all documents, please refresh the page.');
      dispatch({type: WorksheetViewActionType.FAILED_TO_LOAD_STEPS});
      return;
    }

    dispatch({type: WorksheetViewActionType.LOADED, worksheet, steps: loadedDocs});
  };
}
